import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import { AnalyticsProperties, AnalyticsTraits } from "../types";

type InAppMessageEvent = {
  type: string;
  data?: {
    deliveryId?: string;
    messageId?: string;
    actionName?: string;
    actionValue?: string;
    errorMessage?: string;
  };
};

export class CustomerIoAdapter {
  public cioanalytics?: AnalyticsBrowser;
  private onMessageAction?: (actionName: string, actionValue: string) => void;
  private segmentTrack?: (
    event: string,
    properties?: AnalyticsProperties
  ) => void;

  constructor(
    writeKey?: string,
    siteId?: string,
    options?: {
      onMessageAction?: (actionName: string, actionValue: string) => void;
      segmentTrack?: (event: string, properties?: AnalyticsProperties) => void;
    }
  ) {
    this.onMessageAction = options?.onMessageAction;
    this.segmentTrack = options?.segmentTrack;

    if (writeKey && siteId) {
      // Use cioanalytics variable name to avoid conflict with Segment
      this.cioanalytics = AnalyticsBrowser.load(
        { writeKey },
        {
          integrations: {
            "Customer.io In-App Plugin": {
              siteId,
              // Enable anonymous in-app messages
              anonymousInApp: true,
              // Handle in-app message events
              events: ((event: InAppMessageEvent) => {
                this.handleInAppMessageEvent(event);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              }) as any,
            },
          },
        }
      );

      if (import.meta.env.DEV) {
        console.log("Customer.io initialized for in-app messaging");
      }
    } else {
      console.warn(`Failed to initialize customer.io`);
    }
  }

  private handleInAppMessageEvent(event: InAppMessageEvent): void {
    const { type, data } = event;

    // Track all in-app message events to Segment for analytics
    const eventName = type.replace("in-app:", "Customer.io ");
    const properties = {
      deliveryId: data?.deliveryId,
      messageId: data?.messageId,
      actionName: data?.actionName,
      actionValue: data?.actionValue,
      errorMessage: data?.errorMessage,
    };

    this.segmentTrack?.(eventName, properties);

    // Handle message actions for custom routing/navigation
    if (
      type === "in-app:message-action" &&
      data?.actionName &&
      data?.actionValue
    ) {
      this.onMessageAction?.(data.actionName, data.actionValue);
    }
  }

  page(name: string, properties?: AnalyticsProperties): void {
    // Call page for in-app message page rules to work correctly in SPA
    this.cioanalytics?.page(name, properties);
  }

  identify(userId: string, traits?: AnalyticsTraits): void {
    // Identify users for in-app message targeting
    this.cioanalytics?.identify(userId, traits);
  }

  reset(): void {
    // Clear the user identity to disassociate device from user session
    this.cioanalytics?.reset();
  }
}
